import { Component, OnInit, Input, Inject, Directive } from '@angular/core';
import { User } from 'src/app/models/user';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { take, map, catchError } from 'rxjs/operators';
import { Organization } from 'src/app/models/organization';
import { AbstractControl, ValidationErrors, AsyncValidator, NG_ASYNC_VALIDATORS } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Country } from 'src/app/models/country';
import { MixpanelService } from 'src/app/services/mixpanel/mixpanel.service';

@Component({
  selector: 'app-organization-edit-member',
  templateUrl: './organization-edit-member.component.html',
  styleUrls: ['./organization-edit-member.component.scss']
})
export class OrganizationEditMemberComponent implements OnInit {

  @Input()
  public user: User;

  @Input()
  public organization: Organization;

  @Input()
  public currentUser: User;

  // The server's own explanation when it gave one; otherwise the browser's description of the failure
  protected reasonFor(error: { message?: string; error?: { detail?: string } }): string {
    return error?.error?.detail ?? error?.message;
  }

  public editMember() {
    const dialogRef = this.dialog.open(OrganizationEditMemberDialog, {
      width: '600px',
      data: {
        dialogHeader: "Edit user",
        user: { ...this.user },
        organization: { ...this.organization },
        organizationCountries: this.organization.countries.concat(this.organization.notification_countries),
        currentUser: this.currentUser
      }
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result) {
        this.organizationService.saveOrganizationUser(result).subscribe(
          () => this.snackBar.open('User updated', 'Close', { duration: 5000 }),
          (error) => this.snackBar.open('Could not update user: '.concat(this.reasonFor(error)), 'Close')
        )
        this.mixpanelService.track("Edit user");
      }
      return false;
    });
  }

  constructor(
    public dialog: MatDialog,
    public organizationService: OrganizationService,
    protected mixpanelService: MixpanelService,
    protected snackBar: MatSnackBar) { }

  ngOnInit() {
  }

}

@Directive({
  selector: '[duplicateEmailAsyncValidator][ngModel]',
  providers: [{ provide: NG_ASYNC_VALIDATORS, useExisting: DuplicateEmailValidatorDirective, multi: true }]
})
export class DuplicateEmailValidatorDirective implements AsyncValidator {
  @Input('appUserId') userId: number;

  constructor(private organizationService: OrganizationService) { }

  validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return this.organizationService.isValidEmail(control.value, this.userId).pipe(
      map(isValid => (isValid ? false : { 'isDuplicate': true })),
      catchError(() => of(null))
    );
  }
}

export interface MemberDialogData {
  dialogHeader: string;
  user: User;
  organization: Organization;
  organizationCountries: Country[];
  currentUser: User;
}

@Component({
  selector: 'organization-edit-member-dialog',
  templateUrl: 'organization-edit-member-dialog.html',
  styleUrls: ['./organization-edit-member-dialog.scss']
})
export class OrganizationEditMemberDialog implements OnInit {

  public error: string;
  public message: string;
  public selectedCountries: Country[] = [];

  public roleChange(e) {
    if (this.data.user.organization_role == 'owner') {
      this.selectedCountries = [...this.data.organizationCountries];
    }
  }

  public save() {
    // Empty countries array means "all access" for the API
    this.data.user.countries = this.selectedCountries.length === this.data.organizationCountries.length
      ? []
      : this.selectedCountries;
    this.dialogRef.close(this.data.user);
  }

  constructor(
    public dialogRef: MatDialogRef<OrganizationEditMemberDialog>,
    @Inject(MAT_DIALOG_DATA) public data: MemberDialogData,
  ) { }

  ngOnInit() {
    this.selectedCountries = this.data.user.countries?.length
      ? [...this.data.user.countries]
      : [...this.data.organizationCountries];
  }

}
